import React, { useEffect, useState } from 'react';
import { BookOpen, CalendarDays, UserCog, Users, ShieldCheck, PlayCircle, Clock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FirstStepsTutorial } from '@/components/tutorial/FirstStepsTutorial';
import { DailyNavigationTutorial } from '@/components/tutorial/DailyNavigationTutorial';
import { ProfileAccessAppearanceTutorial } from '@/components/tutorial/ProfileAccessAppearanceTutorial';
import { TeamCommunicationTutorial } from '@/components/tutorial/TeamCommunicationTutorial';
import { AdminActionsTutorial } from '@/components/tutorial/AdminActionsTutorial';
import { VideoTutorialPlayer } from '@/components/tutorial/VideoTutorialPlayer';
import { getVisibleVideoTutorials } from '@/components/tutorial/videoTutorials';

const TUTORIALS = [
  {
    id: 'primeros-pasos',
    title: 'Primeros pasos',
    description: 'Aprende a crear, editar y gestionar clientes.',
    icon: BookOpen,
    adminOnly: false,
  },
  {
    id: 'navegacion-gestion-diaria',
    title: 'Navegación y gestión diaria',
    description: 'Aprende a moverte entre días y semanas, utilizar el calendario y gestionar tu ruta diaria.',
    icon: CalendarDays,
    adminOnly: false,
  },
  {
    id: 'perfil-accesos-apariencia',
    title: 'Perfil, accesos y apariencia',
    description: 'Configura tu perfil, accesos rápidos y la apariencia de Rutapp.',
    icon: UserCog,
    adminOnly: false,
  },
  {
    id: 'comunicacion-entre-usuarios',
    title: 'Comunicación entre usuarios',
    description: 'Conoce los perfiles, estados de ruta, avisos y comunicación del equipo.',
    icon: Users,
    adminOnly: false,
  },
  {
    id: 'acciones-de-administrador',
    title: 'Acciones de administrador',
    description: 'Aprende a configurar perfiles, cuentas, permisos y PIN.',
    icon: ShieldCheck,
    adminOnly: true,
  },
];

const TUTORIAL_TITLES = TUTORIALS.reduce((acc, tutorial) => {
  acc[tutorial.id] = tutorial.title;
  return acc;
}, {});

export function TutorialHub({ open, onOpenChange, userRole, initialTarget = null }) {
  const [activeTutorial, setActiveTutorial] = useState(null);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [practiceChain, setPracticeChain] = useState(null);

  const isAdmin = userRole != null && userRole !== 'worker';
  const visibleTutorials = TUTORIALS.filter((tutorial) => !tutorial.adminOnly || isAdmin);
  const visibleVideos = getVisibleVideoTutorials(userRole);

  // Aplica el target de apertura (deep-link) solo cuando el Hub se abre.
  // No se reaplica al navegar dentro del Hub (no depende de activeVideoId/activeTutorial).
  useEffect(() => {
    if (!open || !initialTarget) return;

    if (initialTarget.section === 'videos') {
      const video = initialTarget.videoId
        ? visibleVideos.find((v) => v.id === initialTarget.videoId)
        : visibleVideos[0];
      if (video) {
        setActiveTutorial(null);
        setPracticeChain(null);
        setActiveVideoId(video.id);
      }
    } else if (initialTarget.section === 'tutorials') {
      const tutorial = visibleTutorials.find((t) => t.id === initialTarget.tutorialId);
      if (tutorial) {
        setActiveVideoId(null);
        setPracticeChain(null);
        setActiveTutorial(tutorial.id);
      }
    }
    // target no reconocido / no permitido → vista principal (sin cambios)
  }, [open, initialTarget]);

  const isInTutorial =
    activeTutorial === 'primeros-pasos' ||
    activeTutorial === 'navegacion-gestion-diaria' ||
    activeTutorial === 'perfil-accesos-apariencia' ||
    activeTutorial === 'comunicacion-entre-usuarios' ||
    activeTutorial === 'acciones-de-administrador';

  const activeVideo = activeVideoId
    ? visibleVideos.find((video) => video.id === activeVideoId)
    : null;
  const activeVideoIndex = activeVideo
    ? visibleVideos.findIndex((video) => video.id === activeVideoId)
    : -1;
  const hasNext = activeVideoIndex >= 0 && activeVideoIndex < visibleVideos.length - 1;
  const practiceAvailable =
    activeVideo != null &&
    Array.isArray(activeVideo.relatedTutorials) &&
    activeVideo.relatedTutorials.length > 0;

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) {
      setActiveTutorial(null);
      setActiveVideoId(null);
      setPracticeChain(null);
    }
    onOpenChange(nextOpen);
  };

  const openTutorial = (tutorialId) => {
    setPracticeChain(null);
    setActiveVideoId(null);
    setActiveTutorial(tutorialId);
  };

  const handleTutorialExit = () => {
    setActiveTutorial(null);
    setPracticeChain(null);
  };

  const openVideo = (videoId) => {
    setPracticeChain(null);
    setActiveTutorial(null);
    setActiveVideoId(videoId);
  };

  const handleBackToHub = () => {
    setActiveVideoId(null);
  };

  const openPractice = (video) => {
    const chain = Array.isArray(video.relatedTutorials) ? video.relatedTutorials : [];
    if (chain.length === 0) return;
    setActiveVideoId(null);
    setPracticeChain(chain);
    setActiveTutorial(chain[0]);
  };

  const practiceIndex = practiceChain ? practiceChain.indexOf(activeTutorial) : -1;
  const isPractice = practiceIndex >= 0;
  const nextPracticeId = isPractice ? practiceChain[practiceIndex + 1] : undefined;

  let practiceProps = {};
  if (isPractice) {
    practiceProps = nextPracticeId
      ? {
          completionActionLabel: `Seguir con ${TUTORIAL_TITLES[nextPracticeId]}`,
          onCompletionAction: () => setActiveTutorial(nextPracticeId),
        }
      : {
          completionActionLabel: 'Volver a Tutoriales',
          onCompletionAction: () => {
            setActiveTutorial(null);
            setPracticeChain(null);
          },
        };
  }

  const TutorialComponent =
    activeTutorial === 'navegacion-gestion-diaria'
      ? DailyNavigationTutorial
      : activeTutorial === 'perfil-accesos-apariencia'
        ? ProfileAccessAppearanceTutorial
        : activeTutorial === 'comunicacion-entre-usuarios'
          ? TeamCommunicationTutorial
          : activeTutorial === 'acciones-de-administrador'
            ? AdminActionsTutorial
            : FirstStepsTutorial;

  const dialogClassName = isInTutorial
    ? 'max-h-[85dvh] overflow-y-auto sm:max-w-lg'
    : activeVideo
      ? 'max-h-[90dvh] overflow-y-auto sm:max-w-xl'
      : 'max-h-[90dvh] overflow-y-auto sm:max-w-md py-10 [&>button]:top-12';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={dialogClassName}>
        {isInTutorial ? (
          <TutorialComponent onExit={handleTutorialExit} {...practiceProps} />
        ) : activeVideo ? (
          <VideoTutorialPlayer
            video={activeVideo}
            hasNext={hasNext}
            onNext={() => {
              const nextId = visibleVideos[activeVideoIndex + 1]?.id;
              if (nextId) setActiveVideoId(nextId);
            }}
            onPractice={practiceAvailable ? () => openPractice(activeVideo) : undefined}
            onBack={handleBackToHub}
          />
        ) : (
          <>
            <DialogHeader className="pt-4">
              <DialogTitle>Tutoriales</DialogTitle>
              <DialogDescription>
                Aprende a utilizar Rutapp con vídeos o practicando paso a paso.
                Ninguna acción modificará tus datos reales.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-2">
              <div>
                <h3 className="text-sm font-semibold text-center">Videotutoriales</h3>
                <p className="text-center text-sm text-muted-foreground">
                  Aprende Rutapp viendo cómo funciona.
                </p>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {
                  if (visibleVideos.length > 0) openVideo(visibleVideos[0].id);
                }}
              >
                <PlayCircle className="mr-2 h-4 w-4" />
                Ver todos los vídeos
              </Button>

              {visibleVideos.map((video) => (
                <button
                  key={video.id}
                  type="button"
                  onClick={() => openVideo(video.id)}
                  className="flex cursor-pointer items-center gap-3 rounded-md border p-3 text-left transition-colors hover:bg-accent/50"
                >
                  <div className="flex shrink-0 flex-col items-center gap-1">
                    <img
                      src={video.poster}
                      alt={video.title}
                      className="h-16 w-24 rounded-md bg-muted object-cover"
                    />
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {video.duration}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{video.title}</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {video.description}
                    </p>
                    <span className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-blue-600 dark:text-blue-400">
                      <PlayCircle className="h-4 w-4" />
                      Ver vídeo
                    </span>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-3 flex flex-col gap-2">
              <div>
                <h3 className="text-sm font-semibold text-center">Tutoriales interactivos</h3>
                <p className="text-center text-sm text-muted-foreground">
                  Practica tú mismo las funciones de Rutapp paso a paso.
                </p>
              </div>

              {visibleTutorials.map((tutorial) => {
                const Icon = tutorial.icon;
                const isAvailable =
                  tutorial.id === 'primeros-pasos' ||
                  tutorial.id === 'navegacion-gestion-diaria' ||
                  tutorial.id === 'perfil-accesos-apariencia' ||
                  tutorial.id === 'comunicacion-entre-usuarios' ||
                  tutorial.id === 'acciones-de-administrador';

                const cardContent = (
                  <>
                    <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${isAvailable ? 'bg-blue-50 text-blue-600' : 'bg-muted text-muted-foreground'}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium">{tutorial.title}</p>
                        {!isAvailable && (
                          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                            Próximamente
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {tutorial.description}
                      </p>
                    </div>
                  </>
                );

                const baseClass = 'flex items-start gap-3 rounded-md border p-3 text-left';

                if (isAvailable) {
                  return (
                    <button
                      key={tutorial.id}
                      type="button"
                      onClick={() => openTutorial(tutorial.id)}
                      className={`${baseClass} cursor-pointer transition-colors hover:bg-accent/50`}
                    >
                      {cardContent}
                    </button>
                  );
                }

                return (
                  <div key={tutorial.id} className={`${baseClass} opacity-80`}>
                    {cardContent}
                  </div>
                );
              })}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}